import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Chip from "@mui/material/Chip"
import CircularProgress from "@mui/material/CircularProgress"
import Divider from "@mui/material/Divider"
import IconButton from "@mui/material/IconButton"
import Popover from "@mui/material/Popover"
import Tab from "@mui/material/Tab"
import Tabs from "@mui/material/Tabs"
import Typography from "@mui/material/Typography"
import { styled } from "@mui/material/styles"

import Column, {
  CenterEverythingColumn,
} from "../../../../common/components/Column"
import Row from "../../../../common/components/Row"
import InboxNotificationRow from "./InboxNotificationRow"
import { INBOX_CHANNELS } from "./inboxNotificationTypes"

const TAB_ORDER = [
  INBOX_CHANNELS.email,
  INBOX_CHANNELS.sms,
  INBOX_CHANNELS.voicemail,
]

const TAB_LABELS = {
  [INBOX_CHANNELS.email]: "Email",
  [INBOX_CHANNELS.sms]: "SMS",
  [INBOX_CHANNELS.voicemail]: "Voicemail",
}

const EMPTY_COPY = {
  [INBOX_CHANNELS.email]: "No unread emails.",
  [INBOX_CHANNELS.sms]: "No unread SMS messages.",
  [INBOX_CHANNELS.voicemail]: "No unread voicemails.",
}

export default function InboxNotificationsPopover({
  anchorEl,
  onClose,
  activeChannel,
  onChannelChange,
  counts,
  lists,
  status,
  onClearChannel,
  clearing = false,
}) {
  const activeList = lists?.[activeChannel] || []
  const activeStatus = status?.[activeChannel] || "idle"
  const isInitialLoading = activeStatus === "loading" && activeList.length === 0
  const isError = activeStatus === "error" && activeList.length === 0
  const canClear = activeList.length > 0 && !isInitialLoading && !isError

  function handleClearAll() {
    if (!canClear || clearing) return
    onClearChannel?.(activeChannel)
  }

  return (
    <Popover
      anchorEl={anchorEl}
      open={!!anchorEl}
      onClose={onClose}
      elevation={1}
      anchorOrigin={{
        horizontal: "center",
        vertical: "center",
      }}
      transformOrigin={{
        horizontal: "right",
        vertical: "top",
      }}
      sx={{
        "& .MuiPaper-root": {
          width: "400px",
        },
      }}
    >
      <Column
        sx={{
          position: "relative",
          minHeight: "200px",
          maxHeight: "450px",
        }}
      >
        <HeaderRow>
          <Row sx={{ alignItems: "center", gap: 1 }}>
            <Typography variant="h5" color="text.secondary">
              Inbox
            </Typography>
            <Chip color="primary" label={counts?.total || 0} size="small" />
          </Row>
          <Row sx={{ alignItems: "center", gap: 0.5 }}>
            {canClear ? (
              <Button
                size="small"
                onClick={handleClearAll}
                disabled={clearing}
                aria-label="Clear all inbox notifications"
              >
                {clearing ? "Clearing…" : "Clear all"}
              </Button>
            ) : null}
            <IconButton onClick={onClose} aria-label="Close inbox">
              <CloseOutlinedIcon />
            </IconButton>
          </Row>
        </HeaderRow>

        <Tabs
          value={activeChannel}
          onChange={(_, next) => {
            if (next) onChannelChange?.(next)
          }}
          variant="fullWidth"
          sx={{
            minHeight: 36,
            borderBottom: 1,
            borderColor: "divider",
            "& .MuiTab-root": {
              minHeight: 36,
              py: 0.5,
              fontSize: 13,
            },
          }}
        >
          {TAB_ORDER.map((channel) => (
            <Tab
              key={channel}
              value={channel}
              label={`${TAB_LABELS[channel]} (${counts?.[channel] || 0})`}
            />
          ))}
        </Tabs>

        {isInitialLoading ? (
          <CenterEverythingColumn>
            <CircularProgress size={28} />
            <Typography variant="body2" color="text.secondary">
              Loading inbox…
            </Typography>
          </CenterEverythingColumn>
        ) : isError ? (
          <CenterEverythingColumn>
            <Typography variant="h6" color="text.secondary">
              Could not load {TAB_LABELS[activeChannel].toLowerCase()}.
            </Typography>
          </CenterEverythingColumn>
        ) : activeList.length === 0 ? (
          <CenterEverythingColumn>
            <Typography variant="h6" color="text.secondary">
              {EMPTY_COPY[activeChannel]}
            </Typography>
          </CenterEverythingColumn>
        ) : (
          <Box sx={{ overflowY: "auto" }}>
            {activeList.map((notification, index) => (
              <Box key={`${notification.channel}-${notification.id}`}>
                <InboxNotificationRow
                  notification={notification}
                  onClose={onClose}
                />
                {index < activeList.length - 1 ? <Divider /> : null}
              </Box>
            ))}
          </Box>
        )}
      </Column>
    </Popover>
  )
}

const HeaderRow = styled(Row)(({ theme }) => ({
  justifyContent: "space-between",
  alignItems: "center",
  paddingInline: theme.spacing(2),
  paddingBlock: theme.spacing(1),
  position: "sticky",
  top: 0,
  zIndex: 10000,
  backgroundColor: "white",
  borderBottom: `1px solid ${theme.palette.divider}`,
}))
